/**
 * CR111D: Preview-specific Error Boundary
 * 
 * Wraps ONLY the preview area so crashes don't blank the Admin UI.
 * Shows actionable recovery options: Reset preview, Disable Live Preview.
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw, EyeOff, Bug, ChevronDown, ChevronUp } from "lucide-react";

interface PreviewErrorBoundaryProps {
  children: ReactNode;
  /** Called when user clicks "Reset preview" */
  onReset?: () => void;
  /** Called when user clicks "Disable Live Preview" */
  onDisable?: () => void;
  /** Context for debugging (e.g., "ThemeLabs", "DealCards") */
  previewContext?: string;
}

interface PreviewErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showStack: boolean;
}

export class PreviewErrorBoundary extends Component<PreviewErrorBoundaryProps, PreviewErrorBoundaryState> {
  constructor(props: PreviewErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showStack: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<PreviewErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log with context for diagnostics
    const lastAction = typeof window !== "undefined" ? (window as unknown as { __lastPreviewAction?: string }).__lastPreviewAction : "unknown";
    console.error(
      `[PreviewErrorBoundary] Preview crashed in ${this.props.previewContext || "unknown"} context`,
      {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        lastPreviewAction: lastAction,
      }
    );
    
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  handleDisable = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onDisable?.();
  };

  toggleStack = () => {
    this.setState((prev) => ({ showStack: !prev.showStack }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showStack } = this.state;
      const isDev = import.meta.env.DEV;

      return (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-destructive text-base">
              <AlertTriangle className="h-5 w-5" />
              Preview Crashed
            </CardTitle>
            <CardDescription>
              The live preview encountered an error. The rest of Admin remains functional.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Error Message */}
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
              <p className="text-sm font-mono text-destructive">
                {error?.message || "Unknown error"}
              </p>
            </div>

            {/* Dev-only stack trace */}
            {isDev && (
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={this.toggleStack}
                  className="text-xs text-muted-foreground gap-1 h-7 px-2"
                >
                  <Bug className="h-3 w-3" />
                  {showStack ? "Hide" : "Show"} Stack Trace
                  {showStack ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
                
                {showStack && (
                  <div className="space-y-2">
                    {error?.stack && (
                      <div className="text-xs font-mono p-2 bg-muted rounded-md max-h-32 overflow-auto">
                        <Badge variant="outline" className="mb-1 text-[10px]">Error Stack</Badge>
                        <pre className="whitespace-pre-wrap text-muted-foreground">{error.stack}</pre>
                      </div>
                    )}
                    {errorInfo?.componentStack && (
                      <div className="text-xs font-mono p-2 bg-muted rounded-md max-h-32 overflow-auto">
                        <Badge variant="outline" className="mb-1 text-[10px]">Component Stack</Badge>
                        <pre className="whitespace-pre-wrap text-muted-foreground">{errorInfo.componentStack}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Recovery Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={this.handleReset}
                className="gap-1"
              >
                <RefreshCw className="h-4 w-4" />
                Reset Preview
              </Button>
              {this.props.onDisable && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={this.handleDisable}
                  className="gap-1"
                >
                  <EyeOff className="h-4 w-4" />
                  Disable Live Preview
                </Button>
              )}
            </div>

            {/* Context hint */}
            {this.props.previewContext && (
              <p className="text-xs text-muted-foreground">
                Context: {this.props.previewContext}
              </p>
            )}
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default PreviewErrorBoundary;
